import { removeEmptyFields } from '@speechki-frontend/shared/helpers/functions';

export default {
    name: 'PaginatedResource',
    props: {
        fetchMethod: {
            type: Function,
            required: true
        },
        filterFields: {
            type: [Array, Object],
            default: () => []
        },
        sortingFields: {
            type: Array,
            default: () => []
        },
        pageSize: {
            type: Number,
            default: 20
        },
        initialOrdering: {
            type: String,
            default: ''
        },
        delayFetch: {
            type: Boolean,
            default: false
        },
        defaultFilters: {
            type: Object,
            default: () => ({})
        },
        filterKey: {
            type: String,
            default: ''
        },
        reversed: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            resource: [],
            count: 0,
            page: 1,

            searchString: '',
            filters: {
                ...this.defaultFilters
            },
            ordering: this.initialOrdering,

            loading: false
        };
    },

    computed: {
        filterKeys() {
            if (Array.isArray(this.filterFields)) return this.filterFields;
            return Object.keys(this.filterFields);
        },
        hasFilters() {
            return Object.keys(this.filters).length > 0;
        },
        pages() {
            return Math.ceil(this.count / this.pageSize);
        },
        canLoadMore() {
            return this.page < this.pages;
        },
        isEmpty() {
            return (
                !this.loading &&
                !this.resource.length &&
                !this.searchString &&
                !Object.keys(removeEmptyFields(this.filters)).length
            );
        },
        notFound() {
            return !this.loading && !this.resource.length && (this.searchString || this.hasFilters);
        },
        filterPrefix() {
            if (!this.filterKey) return null;
            return `resource_filter_${this.filterKey}`;
        }
    },

    watch: {
        filters: {
            deep: true,
            handler(newvalue, oldvalue) {
                const diff = {};

                for (let field in newvalue) {
                    if (newvalue[field] !== oldvalue[field]) {
                        diff[field] = {
                            old: oldvalue[field],
                            new: newvalue[field]
                        };
                    }
                }

                this.$emit('filterUpdate', diff);
            }
        },
        resource: {
            deep: true,
            handler(value) {
                this.$emit('resouceUpdated', value);
            }
        }
    },

    created() {
        this.setFiltersFields();

        if (this.delayFetch) return;

        this.fetch();
    },

    methods: {
        setFiltersFields() {
            const values = Array.isArray(this.filterFields) ? {} : this.filterFields;
            const localFilter = this.filterPrefix
                ? sessionStorage.getItem(this.filterPrefix)
                : null;

            this.filterKeys.forEach(field => {
                let { [field]: value = '' } = values;
                if (this.defaultFilters[field]) {
                    value = this.defaultFilters[field];
                }
                this.filters[field] = value;
            });

            if (localFilter) {
                this.filters = {
                    ...(this.filters || {}),
                    ...JSON.parse(localFilter)
                };
            }
        },

        async fetch(extraParams = {}, reload) {
            this.loading = true;

            const params = {
                page_size: this.pageSize,
                page: this.page,
                ...removeEmptyFields(this.filters),
                ...extraParams
            };

            if (this.ordering) {
                params.ordering = this.ordering;
            }

            if (this.searchString) {
                params.search = this.searchString;
            }

            try {
                const { results, count } = await this.fetchMethod(params);

                if (this.page === 1 || reload) {
                    [this.resource, this.count] = [results, count];
                } else {
                    this.resource = [...this.resource, ...results];
                }

                this.$emit('fetched', this.resource);
            } catch (err) {
                console.log(err);
            } finally {
                this.loading = false;
            }
        },

        // fetches N pages at once, setting page
        // for pagination reload
        async reloadPages() {
            const params = {
                page_size: this.page * this.pageSize,
                page: 1
            };

            this.fetch(params, true);
        },

        updateField(field, value) {
            this.filters = {
                ...this.filters,
                [field]: value
            };
        },

        // public methods
        // search
        search(string = '') {
            this.searchString = string;
            this.page = 1;

            this.fetch();
        },

        // filter
        filter(filters = {}) {
            const filterKeys = Object.keys(filters).filter(filter =>
                this.filterKeys.includes(filter)
            );

            if (!filterKeys.length) return;

            filterKeys.forEach(filter => {
                this.updateField(filter, filters[filter]);
            });

            if (this.filterPrefix) {
                sessionStorage.setItem(this.filterPrefix, JSON.stringify(this.filters));
            }

            this.page = 1;
            return this.fetch();
        },

        // sort
        sort(ordering) {
            const field = ordering.replace('-', '');
            if (!field || !this.sortingFields.includes(field)) return;

            this.ordering = ordering;
            this.page = 1;

            return this.fetch();
        },

        // loadmore
        async loadMore() {
            this.loading = true;
            this.page = this.page + 1;
            const result = await this.fetch();
            this.loading = false;
            return result;
        },

        paginate(page) {
            this.page = page;
            return this.fetch();
        }
    },

    render() {
        const { resource = [] } = this;
        return this.$slots.default({
            resource: this.reversed ? resource.slice().reverse() : resource,
            count: this.count,
            page: this.page,
            pageSize: this.pageSize,

            filters: this.filters,

            canLoadMore: this.canLoadMore,
            isEmpty: this.isEmpty,
            notFound: this.notFound,

            searchString: this.searchString,
            loading: this.loading,

            fetch: this.fetch,
            loadMore: this.loadMore,
            filter: this.filter,
            sort: this.sort,
            search: this.search
        });
    }
};
